/**
 * A session's "AI tools" (TMMAPP-0142 L2–L3; TMMAPI-0183 U1): one row per connected AI tool of the
 * caller's, with Off / Read only / Read and write for this session, saved at once (as in Settings).
 * The session view's `agentGrants[]` shows at once; the caller's connections are read for the
 * tools not granted yet. Read and write runs through `useSubscriberAction()` for a GUEST or NONE
 * tier. With no connection it says how to connect one and links to Settings → Connected AI tools.
 * An archived session shows each tool's access read-only. Never opened on Help & feedback.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  agentConnectionsApi,
  type AgentAccessChoice,
  type AgentConnection,
} from '@/api/agentConnections';
import type { AssistantSession } from '@/api/assistant';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { ErrorState } from '@/components/ErrorState';
import { settingsHref } from '@/features/settings/settingsHref';
import { resolveTokens } from '@/theme/theme';
import { useBrand } from '@/theme/useBrand';
import { AgentAccessControl } from './AgentAccessControl';

const TITLE_ID = 'agent-tools-session-title';

interface Row {
  connectionId: number;
  label: string;
  access: AgentAccessChoice;
}

interface SessionAgentToolsDialogProps {
  session: Pick<AssistantSession, 'id' | 'name' | 'status' | 'agentGrants'>;
  /** A choice saved: the session view (its `agentGrants`) is read again. */
  onChanged: () => void;
  onClose: () => void;
}

function rowsOf(connections: readonly AgentConnection[], sessionId: number): Row[] {
  return connections.map((connection) => ({
    connectionId: connection.id,
    label: connection.label,
    access: connection.grants.find((grant) => grant.sessionId === sessionId)?.access ?? 'OFF',
  }));
}

export function SessionAgentToolsDialog({
  session,
  onChanged,
  onClose,
}: SessionAgentToolsDialogProps) {
  const { t } = useTranslation();
  const openSubscribe = useOpenSubscribe();
  // Read and write for a GUEST or NONE tier: this dialog closes, then the purchase opens (DLG-005).
  const purchase = () => {
    onClose();
    openSubscribe();
  };
  const tokens = resolveTokens(useTheme());
  const { productName } = useBrand();
  const readOnly = session.status === 'ARCHIVED';
  const [connections, setConnections] = useState<AgentConnection[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    agentConnectionsApi.list().then(
      (rows) => {
        if (!live) return;
        setConnections(rows);
        setLoadError(null);
      },
      (error: unknown) => {
        if (live) setLoadError(error);
      },
    );
    return () => {
      live = false;
    };
  }, [attempt]);

  // What the session view serves shows at once; the caller's connections complete it.
  const rows: Row[] =
    connections === null
      ? session.agentGrants.map((grant) => ({ ...grant }))
      : rowsOf(connections, session.id);

  async function choose(connectionId: number, choice: AgentAccessChoice) {
    await agentConnectionsApi.setAccess(connectionId, session.id, choice);
    onChanged();
    try {
      setConnections(await agentConnectionsApi.list());
    } catch {
      // The choice saved, but the list didn't re-read: show what the server now holds.
      setConnections(
        (rows) =>
          rows?.map((row) => {
            if (row.id !== connectionId) return row;
            const others = row.grants.filter((grant) => grant.sessionId !== session.id);
            return {
              ...row,
              grants:
                choice === 'OFF'
                  ? others
                  : [
                      ...others,
                      {
                        sessionId: session.id,
                        sessionName: session.name,
                        access: choice,
                        archived: readOnly,
                      },
                    ],
            };
          }) ?? rows,
      );
    }
  }

  const empty = connections !== null && connections.length === 0;

  return (
    <Dialog open onClose={onClose} aria-labelledby={TITLE_ID} fullWidth maxWidth="sm">
      <DialogTitle id={TITLE_ID}>
        {t('agentTools.dialogTitle', { session: session.name })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }} data-agent-help="">
          {t('agentTools.help')}
        </DialogContentText>
        {readOnly && (
          <Typography sx={{ fontSize: 13, color: tokens.text2, mb: 2 }} data-agent-archived="">
            {t('agentTools.archived')}
          </Typography>
        )}
        {connections === null && loadError !== null && rows.length === 0 && (
          <ErrorState
            error={loadError}
            what={t('agentTools.loadError')}
            onRetry={() => setAttempt((value) => value + 1)}
          />
        )}
        {connections === null && loadError === null && rows.length === 0 && (
          <Typography variant="body2">{t('common.loading')}</Typography>
        )}
        {empty && (
          <Stack sx={{ gap: 1 }} data-agent-tools-empty="">
            <Typography variant="body2">{t('agentTools.empty', { productName })}</Typography>
            <Link component={RouterLink} to={settingsHref('sharing')} onClick={onClose}>
              {t('agentTools.settingsLink')}
            </Link>
          </Stack>
        )}
        {rows.length > 0 && (
          <Stack spacing={2} data-agent-session-rows="">
            {rows.map((row) => (
              <Stack
                key={row.connectionId}
                direction={{ xs: 'column', sm: 'row' }}
                sx={{ gap: 1, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                  {row.label}
                </Typography>
                <AgentAccessControl
                  label={t('agentTools.accessForTool', { label: row.label })}
                  value={row.access}
                  readOnly={readOnly}
                  onChoose={(choice) => choose(row.connectionId, choice)}
                  onPurchase={purchase}
                />
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <DialogDoneButton onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
}
